import { Box, Button, Divider, Paper, Stack, TextField, Typography } from "@mui/material";
import { VIEW } from "../../../../utils/breakpoint";
import { useEffect, useState } from "react";
import { useUpdateBuildingMutation } from "../../../../hooks/config-page";
import { Building, BuildingLevel, CreateBuildingResponse } from "../../../../hooks/config-page/index.type";

type EditBuildingFormProps = {
  onSuccess?: (buildingId: string) => void
  editMode?: boolean,
  defaultData?: Building,
  onChangeEditMode?: (editMode: boolean) => void
}

export default function EditBuildingForm({ onSuccess, editMode, defaultData, onChangeEditMode }: EditBuildingFormProps) {

  const { trigger } = useUpdateBuildingMutation({
    buildingId: defaultData?.building_uuid || '',
    onSuccess: (data: CreateBuildingResponse) => {
      onSuccess?.(data.data.building.building_uuid)
    }
  })
  const onSubmit = () => {
    const form = {
      name: buildingName,
      address: buildingAddress,
      levels: newLevelValue
    }
    trigger(form)

    // Need to change based on Backend flow
    // onSuccess?.('123981ac723828')
  }
  const [newLevelValue, setNewLevelValue] = useState<BuildingLevel[]>([]);
  const [buildingName, setBuildingName] = useState<string>('');
  const [buildingAddress, setBuildingAddress] = useState<string>('');

  const [buildingNameError, setBuildingNameError] = useState<string>('');
  const [buildingAddressError, setBuildingAddressError] = useState<string>('');

  const validateBuildingName = (name: string) => {
    if (!name || name.trim() === '') {
      setBuildingNameError('Building name is required');
      return false;
    }
    setBuildingNameError('');
    return true;
  }
  const validateBuildingAddress = (address: string) => {
    if (address.length > 2000) {
      setBuildingAddressError('Building address must be less than 2000 characters');
      return false;
    }
    setBuildingAddressError('');
    return true;
  }

  const isValid = buildingNameError === '' && buildingAddressError === '' && buildingName !== ''

  const handleAddLevel = () => {
    setNewLevelValue(prev => {
      const newLevels = [...prev];
      newLevels.push({
        id: 'new',
        level: newLevels.length + 1,
        level_name: `L${newLevels.length + 1}`
      });
      return newLevels;
    })
  }

  const handleChangeLevelName = (index: number, value: string) => {
    setNewLevelValue(prev => {
      const newLevels = [...prev];
      newLevels[index].level_name = value;
      return newLevels;
    });
  }

  const handleChangeLevelNumber = (index: number, value: number) => {
    setNewLevelValue(prev => {
      const newLevels = [...prev];
      newLevels[index].level = value;
      return newLevels;
    });
  }

  const handleRemoveLevel = (index: number) => {
    setNewLevelValue(prev => {
      const newLevels = [...prev];
      newLevels.splice(index, 1);
      return newLevels;
    });
  }

  const [levelNameError, setLevelNameError] = useState<string>('');

  const validateLevels = () => {
    setLevelNameError('')
    newLevelValue.forEach((level, index) => {
      if (!level.level_name || level.level_name.trim() === '') {
        setLevelNameError('E' + index + '| Level name is required');
      }
    });
  }

  const isLevelError = (index: number) => {
    if (levelNameError === '') return false;

    const parts = levelNameError.split('|');
    if (parts.length !== 2) return false;

    const errorIndex = parseInt(parts[0].replace('E', ''));
    return errorIndex === index;
  }

  useEffect(() => {
    if (!defaultData) {
      return
    }
    setBuildingName(defaultData.building_name)
    setBuildingAddress(defaultData.building_address || '')
    setNewLevelValue(defaultData.building_levels.map(level => ({
      id: level.id,
      level: level.level,
      level_name: level.level_name
    })))
  }, [defaultData])

  const handleEnableEditMode = () => {
    onChangeEditMode?.(true);
  }

  return (
    <Paper elevation={2} sx={(theme) => ({
      padding: '24px',
      overflowX: 'hidden',
      [theme.breakpoints.up(VIEW.TABLET)]: {
        width: '100%'
      }
    })}>
      <Stack spacing={'16px'}>
        <Box sx={(theme) => ({
          display: 'flex',
          gap: '12px',
          [theme.breakpoints.down(VIEW.TABLET)]: {
            flexDirection: 'column'
          },
          [theme.breakpoints.up(VIEW.TABLET)]: {
            alignItems: 'center',
          }
        })}>
          <Typography sx={(theme) => ({
            [theme.breakpoints.up(VIEW.TABLET)]: {
              width: '280px',
            },
            flex: 'none'
          })}>
            Name:
          </Typography>
          {editMode ? (
            <TextField
              size="small"
              sx={{ flex: 'auto' }}
              value={buildingName}
              onChange={(e) => setBuildingName(e.target.value)}
              onBlur={(e) => validateBuildingName(e.target.value)}
              error={!!buildingNameError}
              helperText={buildingNameError}
            />
          ) : (
            <Typography sx={{ flex: 'auto' }}>{buildingName}</Typography>
          )}
        </Box>
        <Box sx={(theme) => ({
          display: 'flex',
          gap: '12px',
          [theme.breakpoints.down(VIEW.TABLET)]: {
            flexDirection: 'column'
          },
          [theme.breakpoints.up(VIEW.TABLET)]: {
            alignItems: 'center',
          }
        })}>
          <Typography sx={(theme) => ({
            [theme.breakpoints.up(VIEW.TABLET)]: {
              width: '280px',
            },
            flex: 'none'
          })}>
            Address (optional) :
          </Typography>
          {
            editMode ? (
              <TextField
                size="small"
                sx={{ flex: 'auto' }}
                value={buildingAddress}
                onChange={(e) => setBuildingAddress(e.target.value)}
                onBlur={(e) => validateBuildingAddress(e.target.value)}
                error={!!buildingAddressError}
                helperText={buildingAddressError}
              />
            ) : (
              <Typography sx={{ flex: 'auto' }}>{buildingAddress || '-'}</Typography>
            )
          }
        </Box>
        <Box sx={(theme) => ({
          display: 'flex',
          gap: '12px',
          [theme.breakpoints.down(VIEW.TABLET)]: {
            flexDirection: 'column'
          },
          [theme.breakpoints.up(VIEW.TABLET)]: {
            // alignItems: 'center',
          }
        })}>
          <Typography sx={(theme) => ({
            [theme.breakpoints.up(VIEW.TABLET)]: {
              width: '280px',
            },
            flex: 'none'
          })}>
            Number of Levels:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px', flex: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '4px', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexDirection: 'column' }}>
                <Typography variant="body1">
                  {newLevelValue.length} Levels
                </Typography>
              </Box>
              {editMode && (
                <Button size="small" variant="outlined" onClick={handleAddLevel}>Add Level</Button>
              )}
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', mt: '12px' }}>
              {newLevelValue.map((level, index) => (
                <Box key={index} sx={{ display: 'flex', gap: '12px', py: '8px', alignItems: 'center' }}>
                  {editMode ? (
                    <TextField
                      size="small"
                      label={`Level Name`}
                      value={level.level_name}
                      onChange={(e) => handleChangeLevelName(index, e.target.value)}
                      onBlur={() => validateLevels()}
                      error={isLevelError(index)}
                      helperText={isLevelError(index) ? levelNameError.split('|')[1] : ''}
                      sx={{ minWidth: '100px', flex: '1', minHeight: '64px' }}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" color="textSecondary">Level Name</Typography>
                      <Typography sx={{ minWidth: '100px', flex: '1' }}>{level.level_name}</Typography>
                    </Box>
                  )}
                  {editMode ? (
                    <TextField
                      size="small"
                      label="Level Number"
                      type="number"
                      value={level.level}
                      onChange={(e) => handleChangeLevelNumber(index, parseInt(e.target.value))}
                      sx={{ maxWidth: '200px', minHeight: '64px', flex: '1' }}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" color="textSecondary">Level Number</Typography>
                      <Typography sx={{ maxWidth: '200px', flex: '1' }}>{level.level}</Typography>
                    </Box>
                  )}
                  {editMode && (
                    <Box sx={{ display: 'flex', alignItems: 'start', minHeight: '64px' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        sx={{ height: '40px' }}
                        onClick={() => handleRemoveLevel(index)}
                      >Remove</Button>
                    </Box>
                  )}
                </Box>
              ))}

              <span>
                {levelNameError}
              </span>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'end' }}>
          {!editMode && (
            <Button
              disabled={!isValid}
              onClick={handleEnableEditMode}
              variant="contained"
            >
              Edit Building
            </Button>
          )}
          {editMode && (
            <>
              <Button
                variant="outlined"
                onClick={() => onChangeEditMode?.(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={!isValid}
                onClick={onSubmit}
              >
                Save
              </Button>
            </>
          )}
        </Box>
      </Stack>
    </Paper>
  )
}